import type { Pilot, PilotRule } from '../schema/pilot.js';

/**
 * Everything the pilot engine needs to decide whether ONE device should
 * see ONE piloted tile. Assembled per-device by the renderer:
 *  - `deviceId` / `tileId` — the stable percentage-bucket inputs.
 *  - `lanIpv4` — the device's own LAN IPv4 (it self-reports this), matched
 *    against subnet rules. Absent → subnet rules fail closed (e.g. a browser
 *    kiosk that can't read its LAN IP, or an APK that doesn't send it yet).
 *  - `tags` — customer-assigned device tags, supplied by the server.
 *    Absent → tag rules fail closed.
 */
export interface PilotContext {
  deviceId: string;
  tileId: string;
  lanIpv4?: string;
  tags?: readonly string[];
}

const DAY_MS = 86_400_000;

/**
 * Decide whether a piloted tile is visible on this device right now.
 *
 * Window first (not-started / graduated), then the rule. Graduation pulls
 * the "everyone sees it" switch ONE day before `to` — a deliberate buffer
 * so a rollout reaches 100% with a day to confirm before the window ends.
 *
 * Fails CLOSED when a rule needs context the device doesn't have (an
 * offline subnet/tag pilot): a non-pilot device must never accidentally
 * get a pilot tile, and a pilot device simply waits until it can resolve.
 *
 * Entitlement (`pilotRollout`) is NOT checked here — the caller skips
 * filtering entirely when the tenant isn't entitled (fail open to "no
 * targeting"). This function assumes the pilot is meant to be honored.
 */
export function isPilotVisible(pilot: Pilot, ctx: PilotContext, nowMs: number): boolean {
  const fromMs = pilot.from !== undefined ? Date.parse(pilot.from) : null;
  const toMs = pilot.to !== undefined ? Date.parse(pilot.to) : null;

  if (fromMs !== null && nowMs < fromMs) return false; // not started
  if (toMs !== null && nowMs >= toMs - DAY_MS) return true; // graduated (one day early)

  return matchesRule(pilot.rule, ctx, nowMs, fromMs);
}

function matchesRule(
  rule: PilotRule,
  ctx: PilotContext,
  nowMs: number,
  fromMs: number | null,
): boolean {
  switch (rule.kind) {
    case 'percent':
      return bucket(ctx.deviceId, ctx.tileId) < rule.pct;
    case 'percentRamp': {
      // Schema guarantees `from` for ramps; guard anyway (start = now → day 0).
      const start = fromMs ?? nowMs;
      const days = Math.max(0, Math.floor((nowMs - start) / DAY_MS));
      const pct = Math.min(100, rule.startPct + rule.perDay * days);
      return bucket(ctx.deviceId, ctx.tileId) < pct;
    }
    case 'subnet':
      return ctx.lanIpv4 !== undefined && ipInAnyCidr(ctx.lanIpv4, rule.cidrs);
    case 'tag': {
      const tags = ctx.tags;
      return tags !== undefined && rule.tags.some((t) => tags.includes(t));
    }
  }
}

/**
 * Stable 0–99 bucket for a (device, tile) pair via FNV-1a. Salting by
 * `tileId` means each pilot draws an independent cohort — the same
 * unlucky 95% don't get skipped by every rollout. Deterministic, so a
 * device's membership never flickers between polls.
 */
export function bucket(deviceId: string, tileId: string): number {
  const s = `${deviceId}:${tileId}`;
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0) % 100;
}

/** True when `ip` (IPv4) falls within any of the CIDR ranges. */
function ipInAnyCidr(ip: string, cidrs: readonly string[]): boolean {
  const addr = ipv4ToInt(ip);
  if (addr === null) return false;
  for (const cidr of cidrs) {
    const slash = cidr.indexOf('/');
    const base = ipv4ToInt(cidr.slice(0, slash));
    const prefix = Number(cidr.slice(slash + 1));
    if (base === null || !Number.isInteger(prefix) || prefix < 0 || prefix > 32) continue;
    // mask = top `prefix` bits. prefix 0 → match all (mask 0).
    const mask = prefix === 0 ? 0 : (0xffffffff << (32 - prefix)) >>> 0;
    if ((addr & mask) >>> 0 === (base & mask) >>> 0) return true;
  }
  return false;
}

/** Parse dotted-quad IPv4 to an unsigned 32-bit int, or null if malformed. */
function ipv4ToInt(ip: string): number | null {
  const parts = ip.split('.');
  if (parts.length !== 4) return null;
  let out = 0;
  for (const part of parts) {
    if (!/^\d{1,3}$/.test(part)) return null;
    const n = Number(part);
    if (n > 255) return null;
    out = (out << 8) | n;
  }
  return out >>> 0;
}
